import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, ArrowUpRight, X } from 'lucide-react';
import { serviceCategories, getAllServices, getAllSubCategories } from '../data/servicesData';
import BookingModal from '../components/BookingModal';

const ServicesPage = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const searchRef = useRef(null);

  const allServices = getAllServices();
  const allSubCategories = getAllSubCategories();
  
  // Filter sub-categories by main category
  const filteredSubCategories = selectedCategory === 'All' 
    ? allSubCategories 
    : allSubCategories.filter(sub => sub.parentCategory === selectedCategory);

  // Search through services and subcategories
  const getSearchResults = () => {
    if (!searchQuery.trim()) return [];
    
    const query = searchQuery.toLowerCase();
    const results = [];
    
    // Search through all services
    allServices.forEach(service => {
      if (service.name.toLowerCase().includes(query)) {
        results.push({
          type: 'service',
          name: service.name,
          price: service.price,
          duration: service.duration,
          category: service.category,
          categorySlug: service.categorySlug,
          subCategory: service.subCategory,
          subCategorySlug: service.subCategorySlug,
          image: service.image
        });
      }
    });
    
    // Search through subcategories
    allSubCategories.forEach(sub => {
      if (sub.name.toLowerCase().includes(query) || 
          sub.description.toLowerCase().includes(query)) {
        // Avoid duplicates
        if (!results.find(r => r.type === 'subcategory' && r.slug === sub.slug)) {
          results.push({
            type: 'subcategory',
            name: sub.name,
            description: sub.description,
            slug: sub.slug,
            parentSlug: sub.parentSlug,
            parentCategory: sub.parentCategory,
            image: sub.image,
            serviceCount: sub.services.length,
            trending: sub.trending
          });
        }
      }
    });
    
    return results.slice(0, 15); // Limit results
  };

  const searchResults = getSearchResults();

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <Helmet>
        <title>Services & Prices | BLR Nail Club - Hair, Skin, Body, Grooming, Bridal in Bangalore</title>
        <meta name="description" content="Explore our premium beauty services: Hair styling, Skin treatments, Body spa, Personal grooming, Bridal packages & more. Best prices in RR Nagar, Basaveshwarnagar & Varthur." />
        <meta name="keywords" content="nail extensions RR Nagar, hair salon Bangalore, skin treatment near me, bridal makeup Bangalore, spa services Basaveshwarnagar" />
        <link rel="canonical" href="https://www.blrnailclub.in/services" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "BLR Nail Club Services",
            "itemListElement": serviceCategories.map((cat, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "Service",
                "name": cat.name,
                "description": cat.description,
                "url": `https://www.blrnailclub.in/services/${cat.slug}`
              }
            }))
          })}
        </script>
      </Helmet>

      <main className="bg-black min-h-screen" data-testid="services-page">
        {/* Hero Section with Search */}
        <section className="pt-32 pb-8 md:pb-12 bg-black">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <p className="text-[#B8860B] text-xs tracking-[0.3em] uppercase mb-4 font-light">What We Offer</p>
            <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-light tracking-wide mb-6">
              Our Services & Prices
            </h1>
            <p className="text-white/60 text-base md:text-lg font-light max-w-2xl mx-auto mb-10">
              From stunning hair transformations to relaxing spa treatments, explore our comprehensive 
              range of premium beauty services with transparent pricing.
            </p>

            {/* Search Bar */}
            <div ref={searchRef} className="relative max-w-xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={20} />
                <input
                  type="text"
                  placeholder="Search for services (e.g., haircut, facial, massage...)"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setIsSearchOpen(true);
                  }}
                  onFocus={() => setIsSearchOpen(true)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg py-4 pl-12 pr-12 text-white placeholder:text-white/40 focus:outline-none focus:border-[#B8860B]/50 transition-colors"
                  data-testid="service-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>

              {/* Search Dropdown */}
              {isSearchOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-[#0d0d0d] border border-white/10 rounded-lg shadow-2xl overflow-hidden z-50 max-h-[70vh] overflow-y-auto">
                  {/* Quick Category Links */}
                  <div className="sticky top-0 bg-[#0d0d0d] border-b border-white/10 p-3 flex gap-2 overflow-x-auto">
                    <span className="text-white/30 text-xs uppercase tracking-wider py-1 px-2 whitespace-nowrap">Browse:</span>
                    {serviceCategories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/services/${cat.slug}`}
                        onClick={() => setIsSearchOpen(false)}
                        className="text-xs px-3 py-1 rounded-full bg-white/5 text-white/70 hover:bg-[#B8860B]/20 hover:text-[#B8860B] transition-colors whitespace-nowrap"
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>

                  {searchQuery ? (
                    // Show search results when typing
                    searchResults.length > 0 ? (
                      <>
                        <div className="px-4 py-3 border-b border-white/10 bg-[#111]">
                          <span className="text-white/50 text-xs tracking-wider uppercase">
                            Search Results ({searchResults.length})
                          </span>
                        </div>
                        {searchResults.map((result, idx) => (
                          <Link
                            key={`${result.type}-${idx}`}
                            to={result.type === 'subcategory' 
                              ? `/services/${result.parentSlug}/${result.slug}`
                              : `/services/${result.categorySlug}/${result.subCategorySlug}`
                            }
                            onClick={() => {
                              setIsSearchOpen(false);
                              setSearchQuery('');
                            }}
                            className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors border-b border-white/5 last:border-b-0"
                            data-testid={`search-result-${idx}`}
                          >
                            <img
                              src={result.image}
                              alt={result.name}
                              className="w-14 h-14 object-cover rounded"
                            />
                            <div className="flex-1 text-left min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="text-white font-light truncate">{result.name}</h4>
                                {result.trending && (
                                  <span className="text-[10px] bg-[#B8860B]/20 text-[#B8860B] px-2 py-0.5 rounded flex-shrink-0">
                                    Trending
                                  </span>
                                )}
                              </div>
                              {result.type === 'service' ? (
                                <p className="text-[#B8860B] text-sm mt-1">
                                  {result.price} <span className="text-white/40">• {result.duration}</span>
                                </p>
                              ) : (
                                <p className="text-white/50 text-sm mt-1 truncate">
                                  {result.serviceCount} treatments • {result.parentCategory}
                                </p>
                              )}
                            </div>
                            <ArrowUpRight className="text-[#B8860B]/50 flex-shrink-0" size={16} />
                          </Link>
                        ))}
                      </>
                    ) : (
                      <div className="p-6 text-center">
                        <p className="text-white/50">No services found for "{searchQuery}"</p>
                        <p className="text-white/30 text-sm mt-2">Try searching for "haircut", "facial", or "massage"</p>
                      </div>
                    )
                  ) : (
                    // Show all categories and subcategories when no search query
                    <>
                      {serviceCategories.map((category) => (
                        <div key={category.id}>
                          <div className="px-4 py-3 bg-[#111] border-b border-white/10 flex items-center justify-between">
                            <span className="text-[#B8860B] text-xs tracking-wider uppercase font-medium">
                              {category.name}
                            </span>
                            <Link 
                              to={`/services/${category.slug}`}
                              onClick={() => setIsSearchOpen(false)}
                              className="text-white/30 text-xs hover:text-[#B8860B] transition-colors"
                            >
                              {category.subCategories.length} categories →
                            </Link>
                          </div>
                          {category.subCategories.slice(0, 4).map((subCat) => (
                            <Link
                              key={subCat.id}
                              to={`/services/${category.slug}/${subCat.slug}`}
                              onClick={() => {
                                setIsSearchOpen(false);
                                setSearchQuery('');
                              }}
                              className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors border-b border-white/5"
                            >
                              <img
                                src={subCat.image}
                                alt={subCat.name}
                                className="w-12 h-12 object-cover rounded"
                              />
                              <div className="flex-1 text-left min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="text-white font-light text-sm truncate">{subCat.name}</h4>
                                  {subCat.trending && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#B8860B] flex-shrink-0"></span>
                                  )}
                                </div>
                                <p className="text-white/40 text-xs mt-0.5">
                                  {subCat.services.length} treatments
                                </p>
                              </div>
                              <span className="text-[#B8860B]/50 text-xs">Explore →</span>
                            </Link>
                          ))}
                          {category.subCategories.length > 4 && (
                            <Link
                              to={`/services/${category.slug}`}
                              onClick={() => setIsSearchOpen(false)}
                              className="block px-4 py-2 text-xs text-white/40 hover:text-[#B8860B] transition-colors text-center border-b border-white/10"
                            >
                              View all {category.subCategories.length} {category.name.toLowerCase()} categories →
                            </Link>
                          )}
                        </div>
                      ))}
                      
                      {/* Popular Searches */}
                      <div className="p-4 bg-[#111] border-t border-white/10">
                        <p className="text-white/30 text-xs uppercase tracking-wider mb-3">Popular Searches</p>
                        <div className="flex flex-wrap gap-2">
                          {['Haircut', 'Facial', 'Manicure', 'Massage', 'Bridal Makeup', 'Keratin'].map((term) => (
                            <button
                              key={term}
                              onClick={() => setSearchQuery(term)}
                              className="text-xs px-3 py-1.5 rounded-full bg-white/5 text-white/60 hover:bg-[#B8860B]/20 hover:text-[#B8860B] transition-colors"
                            >
                              {term}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="py-6 bg-black border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-wrap justify-center gap-2 md:gap-4">
              <button
                onClick={() => setSelectedCategory('All')}
                className={`px-5 py-2 text-sm tracking-wider transition-colors ${
                  selectedCategory === 'All'
                    ? 'bg-[#B8860B] text-black'
                    : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                }`}
                data-testid="category-all"
              >
                All
              </button>
              {serviceCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.name)}
                  className={`px-5 py-2 text-sm tracking-wider transition-colors ${
                    selectedCategory === category.name
                      ? 'bg-[#B8860B] text-black'
                      : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`category-${category.slug}`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Sub-Categories Grid */}
        <section className="py-16 md:py-20 bg-[#0a0a0a]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            {/* Category Title */}
            <div className="mb-10">
              <h2 className="text-2xl md:text-3xl font-light text-white">
                {selectedCategory === 'All' ? 'All Services' : `${selectedCategory} Services`}
              </h2>
              <p className="text-white/50 mt-2">
                {filteredSubCategories.length} categor{filteredSubCategories.length !== 1 ? 'ies' : 'y'} available
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSubCategories.map((subCat) => (
                <Link
                  key={subCat.id}
                  to={`/services/${subCat.parentSlug}/${subCat.slug}`}
                  className="group bg-black border border-white/10 overflow-hidden hover:border-[#B8860B]/50 transition-all duration-300"
                  data-testid={`service-card-${subCat.slug}`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={subCat.image}
                      alt={subCat.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4 flex items-center gap-2">
                      <span className="bg-black/70 text-white/80 px-2 py-1 text-xs tracking-wider">
                        {subCat.parentCategory}
                      </span>
                      {subCat.trending && (
                        <span className="bg-[#B8860B] text-black px-2 py-1 text-xs tracking-wider">
                          Trending
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="text-white text-lg font-light tracking-wide group-hover:text-[#B8860B] transition-colors">
                        {subCat.name}
                      </h3>
                      <ArrowUpRight 
                        className="text-[#B8860B] flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity mt-1" 
                        size={18} 
                      />
                    </div>
                    <p className="text-white/50 text-sm font-light leading-relaxed mb-3 line-clamp-2">
                      {subCat.description}
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-white/10">
                      <span className="text-white/40 text-xs">
                        {subCat.services.length} treatments
                      </span>
                      <span className="text-[#B8860B] text-sm">
                        From {subCat.services[0]?.price || 'Rs. 500'}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Main Categories Section */}
        <section className="py-16 bg-black border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="text-2xl font-light text-white mb-8 text-center">
              Browse by Category
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {serviceCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/services/${cat.slug}`}
                  className="group relative h-40 overflow-hidden rounded-lg"
                >
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition-colors"></div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
                    <span className="text-white text-lg font-light tracking-wider group-hover:text-[#B8860B] transition-colors">
                      {cat.name}
                    </span>
                    <span className="text-white/40 text-xs mt-1">
                      {cat.subCategories.length} categories
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Location SEO Section */}
        <section className="py-16 bg-[#0a0a0a] border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="text-2xl font-light text-white mb-8 text-center">
              Available at Our Bangalore Locations
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link 
                to="/nails-rr-nagar"
                className="bg-white/5 border border-white/10 p-6 text-center hover:border-[#B8860B]/50 transition-colors"
              >
                <h3 className="text-[#B8860B] text-lg mb-2">RR Nagar</h3>
                <p className="text-white/50 text-sm">Full Service Salon • Hair • Skin • Nails</p>
                <p className="text-white/30 text-xs mt-2">Rajarajeshwari Nagar</p>
              </Link>
              <Link 
                to="/nailashes-basaveshwarnagar"
                className="bg-white/5 border border-white/10 p-6 text-center hover:border-[#B8860B]/50 transition-colors"
              >
                <h3 className="text-[#B8860B] text-lg mb-2">Basaveshwarnagar</h3>
                <p className="text-white/50 text-sm">Nails • Lashes • Makeup Services</p>
                <p className="text-white/30 text-xs mt-2">Nailashes Basaveshwarnagar</p>
              </Link>
              <Link 
                to="/nails-gunjur"
                className="bg-white/5 border border-white/10 p-6 text-center hover:border-[#B8860B]/50 transition-colors"
              >
                <h3 className="text-[#B8860B] text-lg mb-2">Varthur (CSK Mall)</h3>
                <p className="text-white/50 text-sm">Complete Beauty Destination</p>
                <p className="text-white/30 text-xs mt-2">CSK Mall, Gunjur</p>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-black border-t border-white/10">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <p className="text-[#B8860B] text-xs tracking-[0.3em] uppercase mb-4 font-light">Ready to Transform?</p>
            <h2 className="text-white text-3xl md:text-5xl font-light tracking-wide mb-6">
              Book Your Appointment Today
            </h2>
            <p className="text-white/60 font-light mb-10 max-w-xl mx-auto">
              Experience the art of beauty at BLR Nail Club. Our expert artisans are ready to 
              help you look and feel your best.
            </p>
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="inline-flex items-center gap-3 bg-[#B8860B] text-black px-10 py-4 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#9A7B0A] transition-colors"
              data-testid="services-cta-book"
            >
              Book Now
              <ArrowUpRight strokeWidth={1.5} size={18} />
            </button>
          </div>
        </section>

        {/* Booking Modal */}
        <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
      </main>
    </>
  );
};

export default ServicesPage;
